import React, {PureComponent} from "react"
import styled from "styled-components"
import ReactPhoneInput from "react-phone-number-input"
import "react-phone-number-input/style.css"
import {Col} from "react-bootstrap"
import {InjectIntl, injectIntl, getResolvedMessage} from "@irati/utils/lang"
import id from "@irati/utils/id"
import Validation, {ValidationInput} from "@irati/utils/validation"
import getCurrentLocale from "@irati/utils/lang/helpers/getCurrentLocale"
import {FormGroup} from "../Form"
import ErrorText from "../ErrorText"
import HintText from "../HintText"
import Label from "./styled/Label"
import InfoTooltip from "./utils/InfoTooltip"
import {guessCountryFromLocale} from "./helpers/phoneInput"

export type PhoneInputOnChange = (value: string) => void

interface Props extends InjectIntl {
  value?: string | null
  onChange?: PhoneInputOnChange
  /** label text / content key */
  label: string
  /** hint text / content key */
  hint?: string
  id?: string
  className?: string
  hideLabel?: boolean
  /** placeholder text / content key */
  placeholder?: string
  /** tooltip text / content key */
  tooltip?: string
  tooltipPosition?: string
  customTooltip?: boolean
  /** is the phone number required or not */
  required?: boolean
  validation?: any
  /** optional default country code */
  country?: string
  /** list of supported countries */
  countries?: string[]
}

export interface PhoneInputProps extends Omit<Props, "validation"> {}

interface State {
  value: string
  errorMsg?: string
  error?: PBError
  blurred: boolean
}

const StyledReactPhoneInput = styled(ReactPhoneInput)`
  .PhoneInputInput {
    border: 0;
    outline: none;
    border-bottom: 1px solid ${({theme}) => theme.colors.greys.light};
  }
`

class PhoneInput extends PureComponent<Props, State> {
  static getDerivedStateFromProps(nextProps: Props) {
    if (nextProps.value) {
      return {value: nextProps.value}
    }

    return {}
  }

  validation = new ValidationInput(this)

  state: State = {
    blurred: false,
    value: ""
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.state.blurred) {
      this.validation.update(prevProps, prevState)
    }
  }

  componentWillUnmount() {
    this.validation.destroy()
  }

  handleChange = (value: string) => {
    this.setState({value, blurred: true})
    if (this.props.onChange) {
      this.props.onChange(value)
    }
  }

  render() {
    const {hint, hideLabel, className, formatMessage, placeholder, tooltip, tooltipPosition, customTooltip, countries} = this.props
    const resolvedPlaceholder = placeholder ? formatMessage({id: placeholder}) : ""
    const resolvedCountryCode = this.props.country || guessCountryFromLocale(getCurrentLocale())

    const {errorMsg, error} = this.state

    const idNameSpace = id("phoneInput", this.props)
    const ids = {
      label: id("label", idNameSpace),
      input: id("input", idNameSpace),
      error: id("copy-error", idNameSpace),
      hint: id("copy-hint", idNameSpace)
    }

    return (
      <FormGroup className={className} validationState={errorMsg ? "error" : undefined}>
        <Col hidden={hideLabel} xs={tooltip ? 10 : 12}>
          <Label id={ids.label} error={errorMsg} htmlFor={ids.input}>
            {getResolvedMessage(this, "label")}
          </Label>
        </Col>
        {tooltip && (
          <Col xs={2}>
            <InfoTooltip tooltip={tooltip} position={tooltipPosition} customIcon={customTooltip} />
          </Col>
        )}
        <Col xs={12}>
          <StyledReactPhoneInput
            id={ids.input}
            defaultCountry={resolvedCountryCode}
            international={!countries}
            countries={countries}
            placeholder={resolvedPlaceholder}
            value={this.state.value}
            onChange={this.handleChange}
          />
        </Col>
        {(errorMsg || error) && (
          <ErrorText id={ids.error} small>
            {getResolvedMessage(this, "errorMsg", this.state) || getResolvedMessage(this, "error")}
          </ErrorText>
        )}
        {hint && (
          <div>
            <HintText id={ids.hint} small>
              {getResolvedMessage(this, "hint")}
            </HintText>
          </div>
        )}
      </FormGroup>
    )
  }
}

function ValidationHoc(props: PhoneInputProps) {
  const p: Props = {...props}
  if (p.required) {
    // We do not want to support stupid validation rules on phone numbers, simply required or not.
    p.validation = Validation.string(props.formatMessage({id: "platform.mfa.phoneNumber.errors.isRequired"}))
  }

  return <PhoneInput {...props} />
}

export default injectIntl<PhoneInputProps>(ValidationHoc)
